import React, { useEffect, useRef, useState } from "react";
import {
  Checkbox,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  PaginationItem,
  Pagination,
} from "@mui/material";
import { format } from "date-fns";
import LoadingSpinner from "../../components/LoadingSpinner";
import EmptyRows from "../../components/reusableComponent/EmptyRows";
import { useNavigate, useLocation } from "react-router-dom";
import { capitalizeFirstLetter } from "../../components/reusableComponent/CapitalLetter";
import { formatDate } from "../../components/reusableComponent/DateFormat";
import {
  getBadgeClasses,
  getIconClasses,
} from "../../components/reusableComponent/IconAndBadgeClasses";
import ConfirmationModal from "../../components/reusableComponent/NonActionableConfirmationModal";

interface IProps {
  ticket: any;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  selectedTickets: any;
  setSelectedTickets: any;
  useData: any;
  ticketWorkflow: any;
  setStoreKey: any;
  value: any;
  setValue: any;
  pageSize?: number;
  setPageSize?: (size: number) => void;
  onPageChange?: (page: number, size: number) => void;
  isTicketLoading: any;
  onNonActionable?: (ticketId: string | number, currentValue: boolean) => void;
}

const TicketStandardView: React.FC<IProps> = ({
  ticket,
  currentPage,
  setCurrentPage,
  selectedTickets,
  setSelectedTickets,
  useData,
  ticketWorkflow,
  setStoreKey,
  value,
  setValue,
  pageSize: propPageSize,
  setPageSize: propSetPageSize,
  onPageChange,
  isTicketLoading,
  onNonActionable,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [selectAll, setSelectAll] = useState(false);
  const [uiData, setUiData] = useState([]);
  const [keyState, setKeyState] = useState([]);
  const [internalPageSize, setInternalPageSize] = useState<number>(
    propPageSize || 50,
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const ticketRowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>(
    {},
  );
  const scrollRestoreCleanupRef = useRef<(() => void) | null>(null);

  const ticketCount = ticket?.count ? ticket?.count : 0;
  const navigate = useNavigate();
  const location = useLocation();

  const pageSize = propPageSize || internalPageSize;
  const setPageSize = propSetPageSize || setInternalPageSize;

  const pageSizeOptions = [25, 50, 100, 200];

  // ✅ Confirmation modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<{
    ticketId: string | number;
    currentValue: boolean;
  } | null>(null);

  const rows =
    ticket?.results?.map((t: any) => ({
      id: t?.id,
      title: t?.title,
      created: format(new Date(t?.created), "yyyy-MM-dd HH:mm"),
      status_type: t?.status_display,
      description: t?.description,
      assignee: t?.action?.assignee?.username,
      raisedBy: t?.created_by,
      priority: t?.action?.disposition?.priority,
      category: t?.action?.disposition?.workflow?.name,
      dueDate: t?.dueDate,
      ticket_channel: t?.ticket_channel,
      is_non_actionable: t?.is_non_actionable ?? false,
    })) || [];

  const storeScrollPosition = (ticketId: string) => {
    const el = scrollContainerRef.current;
    if (el)
      sessionStorage.setItem(
        `ticketStandardScrollPosition_${value}`,
        String(el.scrollTop),
      );
    sessionStorage.setItem(`lastViewedTicket_Standard_${value}`, ticketId);
  };

  const restoreScrollPosition = () => {
    const saved = sessionStorage.getItem(
      `ticketStandardScrollPosition_${value}`,
    );
    if (!saved) return () => {};
    const target = parseInt(saved, 10);
    if (Number.isNaN(target) || target < 0) return () => {};
    const apply = () => {
      const el = scrollContainerRef.current;
      if (el) el.scrollTop = target;
    };
    const delays = [0, 50, 150, 300, 500, 800, 1200, 1800, 2500, 3500];
    const timeouts = delays.map((d) => setTimeout(apply, d));
    requestAnimationFrame(() => requestAnimationFrame(apply));
    return () => timeouts.forEach((t) => clearTimeout(t));
  };

  const clearScrollPosition = () => {
    sessionStorage.removeItem(`ticketStandardScrollPosition_${value}`);
    sessionStorage.removeItem(`lastViewedTicket_Standard_${value}`);
  };

  const handleTicketClick = (ticketId: string) => {
    storeScrollPosition(String(ticketId));
    navigate(`/ticket/information/${ticketId}`);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) setSelectedTickets(rows.map((row) => row.id));
    else setSelectedTickets([]);
  };

  const handleSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: number,
  ) => {
    const updated = event.target.checked
      ? [...selectedTickets, id]
      : selectedTickets.filter((tid) => tid !== id);
    setSelectedTickets(updated);
    if (!event.target.checked) setSelectAll(false);
  };

  const processData = async () => {
    const uiFormattedData = [];
    const keysForState = [];
    if (ticketWorkflow && typeof ticketWorkflow === "object") {
      await Object.entries(ticketWorkflow).forEach(([key, val]) => {
        if (typeof val === "object" && val !== null && !Array.isArray(val)) {
          Object.entries(val).forEach(([nk, nv]) => {
            uiFormattedData.push({ key: nk, value: nv });
            keysForState.push(key);
          });
        } else if (Array.isArray(val)) {
          val.forEach((item) => {
            if (item && typeof item === "object")
              Object.entries(item).forEach(([nk, nv]) => {
                uiFormattedData.push({ key: nk, value: nv });
                keysForState.push(key);
              });
          });
        }
      });
      setUiData(uiFormattedData);
      setKeyState(keysForState);
    } else {
      console.error("ticketWorkflow is undefined or null");
    }
  };

  const truncateDescription = (description: string, maxLength: number) => {
    if (description?.length > maxLength)
      return description.slice(0, maxLength) + "...";
    return description;
  };

  useEffect(() => {
    if (rows.length > 0 && selectedTickets.length === rows.length)
      setSelectAll(true);
    else setSelectAll(false);
  }, [selectedTickets, rows]);

  useEffect(() => {
    processData();
  }, [ticketWorkflow]);

  useEffect(() => {
    setSelectAll(false);
    setSelectedTickets([]);
  }, [currentPage]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
      setTotalPages(Math.ceil(ticket?.count / pageSize));
    }, 3000);
    return () => clearTimeout(timer);
  }, [currentPage, ticket, pageSize]);

  useEffect(() => {
    const canRestore =
      !loading && !isTicketLoading && ticket?.results?.length > 0;
    if (canRestore) {
      const timer = setTimeout(() => {
        const cleanup = restoreScrollPosition();
        if (typeof cleanup === "function")
          scrollRestoreCleanupRef.current = cleanup;
      }, 50);
      return () => {
        clearTimeout(timer);
        scrollRestoreCleanupRef.current?.();
        scrollRestoreCleanupRef.current = null;
      };
    }
  }, [loading, isTicketLoading, ticket?.results, value]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const el = scrollContainerRef.current;
      if (el)
        sessionStorage.setItem(
          `ticketStandardScrollPosition_${value}`,
          String(el.scrollTop),
        );
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [value]);

  useEffect(() => {
    if (!location.pathname.includes("/ticket")) clearScrollPosition();
  }, [location.pathname]);

  const handlePageSizeChange = (event: any) => {
    const newPageSize = Number(event.target.value);
    setPageSize(newPageSize);
    setCurrentPage(1);
    clearScrollPosition();
    sessionStorage.setItem("ticketStandardPageSize", newPageSize.toString());
    onPageChange?.(1, newPageSize);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    clearScrollPosition();
    setCurrentPage(page);
    onPageChange?.(page, pageSize);
  };

  // ✅ Fires after user clicks "Confirm"
  const handleConfirmNonActionable = () => {
    if (confirmPayload && onNonActionable) {
      onNonActionable(confirmPayload.ticketId, confirmPayload.currentValue);
    }
    setConfirmPayload(null);
  };

  const tenantName = window.location.hostname.split(".")[0]?.toLowerCase();

  const tableHeaders = [
    "Ref ID",
    "Title",
    "Created at",
    "Status",
    "Ticket channel",
    "Description",
    "Assignee",
    "Raised by",
    "Priority",
    "Workflow",
    ...(onNonActionable ? ["Actions"] : []),
  ];

  const cellSx = {
    width: "11%",
    minWidth: 120,
    maxWidth: 180,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="tabs-header-section">
            <div className="tabs-header-content">
              <Tabs
                value={uiData.findIndex((item) => item.key === value)}
                onChange={(event, newValue) => {
                  clearScrollPosition();
                  setStoreKey(keyState[newValue]);
                  setValue(uiData[newValue].key);
                  setCurrentPage(1);
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  flex: 1,
                  minHeight: "44px",
                  backgroundColor: "#ffffff",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#6366f1",
                    height: "2px",
                  },
                  "& .MuiTab-root": {
                    textTransform: "none",
                    minHeight: "44px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#6c757d",
                    padding: "10px 20px",
                    transition: "all 0.2s ease",
                    minWidth: "auto",
                    "&:hover": { color: "#495057", backgroundColor: "#f8f9fa" },
                    "&.Mui-selected": { color: "#6366f1", fontWeight: 600 },
                  },
                  "& .MuiTabs-scrollButtons": {
                    color: "#6c757d",
                    "&.Mui-disabled": { opacity: 0.3 },
                  },
                }}
              >
                {uiData.map((ticketsInfo, index) => (
                  <Tooltip
                    key={index}
                    title={`${ticketsInfo.key} (${ticketsInfo.value})`}
                    arrow
                  >
                    <Tab
                      key={index}
                      label={truncateDescription(
                        `${ticketsInfo.key} (${ticketsInfo.value})`,
                        25,
                      )}
                    />
                  </Tooltip>
                ))}
              </Tabs>
            </div>
          </div>

          <div
            ref={scrollContainerRef}
            style={{
              height: "calc(100vh - 400px)",
              overflow: "auto",
              overflowX: "hidden",
            }}
          >
            <TableContainer
              component={Paper}
              style={{ cursor: "pointer" }}
              ref={tableContainerRef}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      sx={{ width: 48, minWidth: 48 }}
                    >
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                        inputProps={{ "aria-label": "select all" }}
                      />
                    </TableCell>
                    {tableHeaders.map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontWeight: 600,
                          fontSize: "14px",
                          width: "11%",
                          minWidth: 120,
                          maxWidth: 180,
                          textAlign: "left",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isTicketLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={tableHeaders.length + 1}
                        style={{ textAlign: "center" }}
                      >
                        <LoadingSpinner />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length + 1}>
                        <EmptyRows value={""} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row: any) => {
                      return (
                        <TableRow
                          key={row.id}
                          ref={(el) => (ticketRowRefs.current[row.id] = el)}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                          onClick={() => handleTicketClick(row.id)}
                          hover
                        >
                          <TableCell
                            padding="checkbox"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ width: 48, minWidth: 48 }}
                          >
                            <Checkbox
                              checked={selectedTickets.includes(row.id)}
                              onChange={(e) => handleSelect(e, row.id)}
                              inputProps={{ "aria-label": "select" }}
                            />
                          </TableCell>
                          <TableCell sx={cellSx}>{row.id}</TableCell>
                          <TableCell sx={cellSx}>
                            {capitalizeFirstLetter(row.title)}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {formatDate(row.created)}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {capitalizeFirstLetter(row.status_type)}
                          </TableCell>
                          <TableCell
                            sx={{ ...cellSx, verticalAlign: "middle" }}
                          >
                            <div
                              className={`${getBadgeClasses(
                                row.ticket_channel,
                              )}`}
                              style={{
                                width: "35px",
                                height: "35px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderRadius: "50%",
                              }}
                            >
                              <i
                                className={getIconClasses(row.ticket_channel)}
                                data-bs-toggle="tooltip"
                                title={row?.ticket_channel}
                              ></i>
                            </div>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Tooltip
                              title={
                                capitalizeFirstLetter(row.description) || ""
                              }
                              arrow
                            >
                              <span>
                                {capitalizeFirstLetter(row.description)}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {capitalizeFirstLetter(
                              row.assignee === undefined
                                ? "Unassigned"
                                : row.assignee,
                            )}
                          </TableCell>
                          <TableCell sx={cellSx}>
                          {capitalizeFirstLetter(row?.raisedBy?.username || row?.raisedBy || "-")}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            {capitalizeFirstLetter(row.priority)}
                          </TableCell>
                          <TableCell sx={cellSx}>{row.category}</TableCell>

                          {/* ✅ Non-actionable cell — opens shared ConfirmationModal */}
                          {onNonActionable && (
                            <TableCell
                              sx={{
                                width: 60,
                                minWidth: 60,
                                textAlign: "center",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Tooltip
                                title={
                                  row.is_non_actionable
                                    ? "Mark as actionable"
                                    : "Mark as non-actionable"
                                }
                                arrow
                              >
                                <span>
                                  <button
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      padding: "4px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 28,
                                      height: 28,
                                    }}
                                    onClick={() => {
                                      setConfirmPayload({
                                        ticketId: row.id,
                                        currentValue: row.is_non_actionable,
                                      });
                                      setConfirmModalOpen(true);
                                    }}
                                    aria-label={
                                      row.is_non_actionable
                                        ? "Mark as actionable"
                                        : "Mark as non-actionable"
                                    }
                                  >
                                    <i
                                      className={`bi ${
                                        row.is_non_actionable
                                          ? "bi bi-ticket-perforated-fill fs-2"
                                          : "bi bi-ticket-perforated fs-2"
                                      }`}
                                      style={{
                                        color: row.is_non_actionable
                                          ? "#059669"
                                          : "#6B7280",
                                        fontSize: "16px",
                                      }}
                                    />
                                  </button>
                                </span>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <div style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
            <div
              className="d-flex justify-content-between align-items-center mt-4 px-2"
              style={{ marginBottom: "8px" }}
            >
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: "14px", whiteSpace: "nowrap" }}>
                  Show
                </span>
                <select
                  className="form-select form-select-sm"
                  style={{
                    width: "60px",
                    fontSize: "14px",
                    padding: "2px 6px",
                  }}
                  name="perpage"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#495057",
                  }}
                >
                  per page
                </span>
              </div>
              <div
                className="d-flex align-items-center"
                style={{ paddingRight: "8px" }}
              >
                <Pagination
                  sx={{
                    display: "flex",
                    justifyContent: "end",
                    "& .MuiPaginationItem-root": {
                      fontSize: "14px",
                      minWidth: "36px",
                      height: "36px",
                      margin: "0 2px",
                      borderRadius: "6px",
                      border: "1px solid #e5e7eb",
                      color: "#374151",
                      "&:hover": {
                        backgroundColor: "#f9fafb",
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#6366f1",
                        color: "#ffffff",
                        borderColor: "#6366f1",
                        "&:hover": {
                          backgroundColor: "#4f46e5",
                          borderColor: "#4f46e5",
                        },
                      },
                    },
                  }}
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                  renderItem={(item) => {
                    if (item.type === "page") {
                      const near =
                        Math.abs(item.page - currentPage) <= 1 ||
                        item.page === 1 ||
                        item.page === totalPages;
                      return near ? <PaginationItem {...item} /> : null;
                    }
                    return <PaginationItem {...item} />;
                  }}
                />
              </div>
            </div>
          </div>

          <ConfirmationModal
            isOpen={confirmModalOpen}
            onClose={() => {
              setConfirmModalOpen(false);
              setConfirmPayload(null);
            }}
            onConfirm={handleConfirmNonActionable}
            title="Confirm Action"
            message={
              <>
                Are you sure you want to{" "}
                <strong>
                  {confirmPayload?.currentValue
                    ? "mark as actionable"
                    : "mark as non-actionable"}
                </strong>
                ?
              </>
            }
          />
        </>
      )}
    </>
  );
};

export default TicketStandardView;